import React, {useMemo} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {ThemeProvider} from '@mui/system';
import {
  selectMetadataEditMetadataHasOpenWorkLevelChangeRequests,
  selectMetadataEditOrder,
} from 'services/metadata/metadataEditSlice';
import {useAppSelector} from 'store/hooks';
import {Scene} from 'components';
import {useLayoutTheme} from 'components/scene/hooks/useLayoutTheme';
import {useSceneExtensionsContext} from 'sceneExtensions/contexts/SceneExtensionsContext';
import {
  ExpressionCard,
  NewExpressionWithManifestationCard,
  WorkCard,
  WorkHeader,
} from './components';
import {
  WorkEditStateContext,
  WorkLockContext,
  useWorkEditStateContextProviderValue,
  useWorkSizeContext,
} from './contexts';
import {useManifestationId, useWorkTitles} from './hooks';
import {useManifestationDesignatorParam} from './hooks/useManifestationDesignatorParam';

type Props = {
  workId: string;
};

export const WorkScene: React.FC<Props> = ({workId}) => {
  const manifestationDesignator = useManifestationDesignatorParam();
  const initialManifestationId = useManifestationId(manifestationDesignator);

  const expressionOrder = useAppSelector(selectMetadataEditOrder);
  const {currentExtension} = useSceneExtensionsContext();
  const {title} = useWorkTitles();
  const hasWorkChangeRequests = useAppSelector(
    selectMetadataEditMetadataHasOpenWorkLevelChangeRequests,
  );

  const {workSize} = useWorkSizeContext();

  const {
    workLayoutSize,
    expressionLayoutSize,
    workThemeSize,
    expressionThemeSize,
    columns,
    transitionStyle,
  } = useMemo(() => {
    // Multiply max number of Grid columns (from default 12) to
    // allow smaller size on minimized WorkCard/ExpressionCards.
    const columns = 24;
    const columnsMultiplier = 2;
    const normalWorkLayoutSize = hasWorkChangeRequests
      ? 7 * columnsMultiplier
      : 5 * columnsMultiplier;
    const workLayoutSize =
      workSize === 'minimized'
        ? 1 // Only 1 grid column to minimized WorkCard
        : workSize === 'maximized'
          ? columns // ExpressionCards are hidden
          : normalWorkLayoutSize;
    const expressionLayoutSize = columns - workLayoutSize;

    // Keep hidden column's theme stable, as a new theme re-renders its whole subtree
    const workThemeSize =
      workSize === 'minimized' ? normalWorkLayoutSize : workLayoutSize;
    const expressionThemeSize =
      workSize === 'maximized'
        ? columns - normalWorkLayoutSize
        : expressionLayoutSize;

    return {
      workLayoutSize,
      expressionLayoutSize,
      workThemeSize,
      expressionThemeSize,
      columns,
      transitionStyle: {
        transition: 'all .5s ease-in-out',
      },
    };
  }, [hasWorkChangeRequests, workSize]);

  const workTheme = useLayoutTheme(workThemeSize, 12);
  const expressionTheme = useLayoutTheme(expressionThemeSize, 12);

  const {editState, lockProps} = useWorkEditStateContextProviderValue(workId);

  return (
    // TODO: Consider to move WorkEditStateContextProvider to just enclose WorkCard
    <WorkEditStateContext.Provider value={editState}>
      <WorkLockContext.Provider value={lockProps}>
        <Scene
          browserTitle={title}
          rightPanelConfig={currentExtension?.panelConfig}>
          <Scene.Header>
            <WorkHeader />
          </Scene.Header>
          <Scene.Content>
            <Box component={Grid} container spacing={2} columns={columns}>
              <Box
                component={Grid}
                size={{
                  xs: columns,
                  sm: columns,
                  md: workLayoutSize,
                }}
                style={transitionStyle}>
                <ThemeProvider theme={workTheme}>
                  <WorkCard workId={workId} />
                </ThemeProvider>
              </Box>
              <Box
                component={Grid}
                size={{
                  xs: columns,
                  sm: columns,
                  md: expressionLayoutSize,
                }}
                style={transitionStyle}
                sx={
                  workSize === 'maximized' ? {display: {md: 'none'}} : undefined
                }>
                <ThemeProvider theme={expressionTheme}>
                  <Grid container spacing={2}>
                    {expressionOrder.map(({expressionId, type, content}) => (
                      <Grid key={expressionId} size={{xs: 12}}>
                        {type === 'existing' ? (
                          <ExpressionCard
                            workId={workId}
                            expressionId={expressionId}
                            content={content}
                            initialManifestationId={initialManifestationId}
                            readonly={editState.readonly}
                          />
                        ) : (
                          <NewExpressionWithManifestationCard
                            workId={workId}
                            expressionId={expressionId}
                            content={content}
                          />
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </ThemeProvider>
              </Box>
            </Box>
          </Scene.Content>

          {currentExtension ? (
            <Scene.RightPanel>
              {currentExtension.renderRightPanel()}
            </Scene.RightPanel>
          ) : null}
        </Scene>
      </WorkLockContext.Provider>
    </WorkEditStateContext.Provider>
  );
};
